import React from 'react';
import { Accessibility, Clock3 } from 'lucide-react';
import { getTranslatedLanguageWithUserSettings } from '@/services/GNRL';
import { translate } from '@/services/TranslationService';
import { Typography } from '@/components/novoville';
import { Separator } from '@/components/ui/separator';
import MyCityPopup from './MyCityPopup';

const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const humanize = (value) =>
  String(value || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (character) => character.toUpperCase());

const translateWithFallback = (key, fallback) => {
  const translated = translate(key);
  return translated && translated !== key ? translated : humanize(fallback);
};

const getLocalizedDescription = (description) => {
  if (!description) return '';
  if (typeof description === 'string') return description;

  const language = getTranslatedLanguageWithUserSettings();
  return (
    description[language] ||
    (language === 'el' ? description.gr : null) ||
    description.en ||
    Object.values(description).find((value) => typeof value === 'string') ||
    ''
  );
};

const formatTime = (value) => {
  if (!value) return '';
  return String(value).slice(0, 5);
};

const getDayName = (day) => {
  const numericDay = Number(day);
  const fallback = Number.isInteger(numericDay)
    ? DAY_NAMES[numericDay] || day
    : day;
  const translationKey = String(fallback || '').toLowerCase();
  return translateWithFallback(translationKey, fallback);
};

const getOpeningRanges = (day) => {
  const ranges = Array.isArray(day?.open_times)
    ? day.open_times
    : Array.isArray(day?.times)
      ? day.times
      : [];

  return ranges
    .map((range) => ({
      start: range?.start || range?.open?.time,
      end: range?.end || range?.close?.time,
    }))
    .filter((range) => range.start || range.end);
};

const GeozoneInfoWindow = ({
  coordinates,
  selectedGeozone,
  anchor = 'right',
  maxHeight = 520,
  closeInfoWindow,
}) => {
  const properties = selectedGeozone?.properties || {};
  const title = translateWithFallback(
    `placemark_type_${properties.type}`,
    properties.type
  );
  const accessibility = translateWithFallback(
    `accessibility_${properties.accessibility_level}`,
    properties.accessibility_level
  );
  const description = getLocalizedDescription(properties.description);
  const photo = Array.isArray(properties.photo)
    ? properties.photo[0]
    : properties.photo;
  const schedule = Array.isArray(properties.open_times)
    ? properties.open_times
    : properties.open_times && typeof properties.open_times === 'object'
      ? Object.values(properties.open_times)
      : [];

  return (
    <MyCityPopup
      coordinates={coordinates}
      anchor={anchor}
      maxHeight={maxHeight}
      closeInfoWindow={closeInfoWindow}
      closeLabel={translateWithFallback('Close', 'Close')}
    >
      {photo && (
        <img
          className="infoWindowImage rounded-[6px]"
          src={photo}
          alt={title}
        />
      )}

      <Typography
        variant="semibold_14"
        className="text-gray-800 break-words pt-[8px]"
      >
        {title}
      </Typography>

      {(description || properties.accessibility_level) && (
        <Separator className="my-[8px]" />
      )}

      {description && (
        <Typography variant="regular_14" className="text-gray-800">
          {description}
        </Typography>
      )}

      {properties.accessibility_level && (
        <div
          className="mt-[10px] flex items-center gap-[8px] rounded-[6px] px-[10px] py-[8px]"
          style={{ backgroundColor: properties.color || '#d6f5ef' }}
        >
          <Accessibility className="h-[16px] w-[16px] shrink-0 text-gray-800" />
          <Typography variant="semibold_14" className="text-gray-800">
            {accessibility}
          </Typography>
        </div>
      )}

      {schedule.length > 0 && (
        <div className="mt-[14px] flex flex-col gap-[8px]">
          <div className="flex items-center gap-[8px]">
            <Clock3 className="h-[16px] w-[16px] text-gray-600" />
            <Typography variant="semibold_14" className="text-gray-800">
              {translateWithFallback('Hours', 'Hours')}
            </Typography>
          </div>
          {schedule.map((day, index) => {
            const ranges = getOpeningRanges(day);
            const hours =
              day?.is_closed || day?.is_open === false
                ? translateWithFallback('BottomSheet_closed', 'Closed')
                : day?.is_open_all_day
                  ? translateWithFallback('BottomSheet_All-day', 'All day')
                  : ranges
                      .map(
                        (range) =>
                          `${formatTime(range.start)} - ${formatTime(range.end)}`
                      )
                      .join(', ');

            return (
              <div
                key={`${day?.day || 'day'}-${index}`}
                className="flex justify-between gap-[12px]"
              >
                <Typography variant="regular_14" className="text-gray-700">
                  {getDayName(day?.day)}
                </Typography>
                <Typography
                  variant="semibold_14"
                  className="text-right text-gray-800"
                >
                  {hours || '-'}
                </Typography>
              </div>
            );
          })}
        </div>
      )}
    </MyCityPopup>
  );
};

export default GeozoneInfoWindow;
